import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MdDelete, MdEdit } from 'react-icons/md';

import Layout from '../Layout';
import {
  chercherIntermediaire, creerIntervenant, inviter, inviterARelier,
  listerCompagnies, listerIntervenants, listerLiaisons, modifierIntervenant,
  supprimerIntervenant,
} from '../api';

/**
 * Qualités possibles.
 *
 * `role1` est la qualité vis-à-vis du sinistre, `role2` la précision. La
 * structure vient de l'outil expert et ne bouge pas : on remplit les mêmes
 * champs, avec des mots que l'assuré comprend.
 */
const ROLES = [
  { valeur: 'assuré', libelle: 'Assuré',
    aide: "Celui au nom de qui le contrat est souscrit.",
    precisions: ['Propriétaire occupant', 'Propriétaire non occupant',
                 'Locataire', 'Copropriétaire', 'Usufruitier'] },
  { valeur: 'intermédiaire', libelle: 'Intermédiaire',
    aide: "Votre courtier ou votre agent.",
    precisions: ['Courtier', 'Agent', 'Mandataire'] },
  { valeur: 'expert', libelle: 'Expert',
    aide: "Le bureau d'expertise qui instruit le dossier.",
    precisions: ["Expert de l'assuré", 'Expert de la compagnie',
                 'Contre-expert'] },
  { valeur: 'tiers lésé', libelle: 'Tiers lésé',
    aide: "La personne à qui vous avez causé un dommage.",
    precisions: ['Voisin', 'Propriétaire', 'Locataire', 'Autre'] },
  { valeur: 'responsable', libelle: 'Responsable présumé',
    aide: "Celui à qui vous imputez le sinistre.",
    precisions: ['Voisin', 'Propriétaire', 'Entrepreneur', 'Inconnu'] },
  { valeur: 'compagnie', libelle: "Compagnie d'assurance",
    aide: "Votre assureur, et le service qui suit ce sinistre.",
    precisions: ['Service sinistres', 'Gestionnaire', 'Expert mandaté'] },
  { valeur: 'entrepreneur', libelle: 'Entrepreneur',
    aide: "L'entreprise qui a établi un devis ou réalisé les travaux.",
    precisions: [] },
  { valeur: 'témoin', libelle: 'Témoin', aide: '', precisions: [] },
];

// Qui suit le dossier sur la plateforme, et à quel titre : le serveur le
// déduit des invitations acceptées et des dossiers reliés, pas des adresses.
const COULEURS_PLATEFORME = {
  titulaire: 'primary',
  acces: 'success',
  relie: 'success',
  invite: 'info',
  retire: 'default',
};

const VIDE = {
  role1: 'intermédiaire', role2: '', reference: '', genre: '', firstname: '',
  name: '', name_company: '', entreprise_number: '', mail: '', phone: '',
  street: '', number: '', postcode: '', city: '', country: '',
};

const roleDe = (valeur) =>
  ROLES.find((r) => r.valeur === (valeur || '').toLowerCase());

function nomAffiche(personne) {
  if (personne.name_company) return personne.name_company;
  const complet = [personne.firstname, personne.name].filter(Boolean).join(' ');
  return complet || 'Sans nom';
}

// Ceux qui instruisent le sinistre de leur côté ont leur propre dossier : on
// relie les deux plutôt que d'ouvrir le sien.
const ONT_LEUR_DOSSIER = ['expert', 'compagnie'];

/**
 * Inviter un intervenant sur la plateforme.
 *
 * Deux façons, qui ne disent pas la même chose : lui ouvrir ce dossier (on
 * travaille ensemble sur le même), ou relier son dossier au vôtre (chacun
 * garde le sien et n'échange que ce qu'il envoie). Relier, et accorder la
 * modification, sont réservés au titulaire du dossier.
 */
function Invitation({ personne, titulaire, expertiseId, onFermer, onInvite }) {
  const [email, setEmail] = useState(personne.mail || '');
  const [mode, setMode] = useState(
    titulaire && ONT_LEUR_DOSSIER.includes((personne.role1 || '').toLowerCase())
      ? 'relier' : 'acces');
  const [niveau, setNiveau] = useState('LECTURE');
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');

  const envoyer = async () => {
    setOccupe(true);
    setErreur('');
    try {
      const reponse = mode === 'relier'
        ? await inviterARelier(expertiseId, personne.id, email.trim())
        : await inviter(expertiseId, { email: email.trim(), level: niveau,
                                       person_expertise: personne.id });
      onInvite(reponse.courriel === false
        ? { severite: 'warning', texte: `L'invitation est prête mais le courriel n'est pas parti. Envoyez ce lien à ${email.trim()} : ${reponse.lien}` }
        : { severite: 'success', texte: `Invitation envoyée à ${email.trim()}.` });
    } catch (e) {
      setErreur(e?.response?.data?.detail || e?.response?.data?.email?.[0]
        || "L'invitation n'a pas pu être envoyée.");
      setOccupe(false);
    }
  };

  return (
    <Dialog open onClose={onFermer} fullWidth maxWidth="sm">
      <DialogTitle>Inviter {nomAffiche(personne)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erreur && <Alert severity="error">{erreur}</Alert>}
          <TextField
            label="Son adresse électronique" type="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            helperText="L'invitation ne s'ouvrira qu'avec un compte à cette adresse : transférer le lien ne donne rien." />

          {titulaire && (
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
              <FormControlLabel value="acces" control={<Radio />} label={(
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Lui ouvrir ce dossier
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Vous travaillez ensemble sur le même dossier : votre courtier, un proche.
                  </Typography>
                </Box>
              )} />
              <FormControlLabel value="relier" control={<Radio />} label={(
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Relier son dossier au vôtre
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Chacun garde son dossier et n'échange que ce qu'il envoie : un expert, la compagnie.
                  </Typography>
                </Box>
              )} />
            </RadioGroup>
          )}

          {mode === 'acces' && (
            <TextField select label="Ce qu'il pourra faire" value={niveau}
                       onChange={(e) => setNiveau(e.target.value)}
                       helperText={niveau === 'GESTION'
                         ? 'Il peut déclarer, chiffrer, déposer des pièces.'
                         : 'Il voit le dossier, il ne le modifie pas.'}>
              <MenuItem value="LECTURE">Lecture seule</MenuItem>
              {titulaire && <MenuItem value="GESTION">Lecture et modification</MenuItem>}
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFermer}>Annuler</Button>
        <Button variant="contained" onClick={envoyer}
                disabled={occupe || !email.includes('@')}>
          {occupe ? 'Envoi…' : 'Inviter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Le formulaire d'un intervenant, en dialogue pour ne pas noyer la liste. */
function Formulaire({ ouvert, valeurs, onChange, onFermer, onEnregistrer,
                     occupe, erreur }) {
  const role = roleDe(valeurs.role1);
  const [recherche, setRecherche] = React.useState('');
  const [trouves, setTrouves] = React.useState([]);

  // Le registre ne sert que pour un intermédiaire : c'est le seul intervenant
  // dont la qualité est publique et vérifiable.
  const registre = valeurs.role1 === 'intermédiaire';
  const estCompagnie = valeurs.role1 === 'compagnie';
  const [compagnies, setCompagnies] = React.useState([]);

  React.useEffect(() => {
    if (!estCompagnie) return;
    listerCompagnies().then(setCompagnies).catch(() => setCompagnies([]));
  }, [estCompagnie]);

  React.useEffect(() => {
    if (!registre) { setTrouves([]); return undefined; }
    let vivant = true;
    const minuteur = setTimeout(() => {
      chercherIntermediaire(recherche)
        .then((r) => vivant && setTrouves(r))
        .catch(() => vivant && setTrouves([]));
    }, 300);
    return () => { vivant = false; clearTimeout(minuteur); };
  }, [recherche, registre]);

  const reprendreDuRegistre = (choix) => {
    if (!choix) return;
    // La qualité vient du registre, jamais d'une déclaration : un agent est
    // agréé pour des compagnies nommées, un courtier ne l'est pour aucune.
    // L'adresse arrive découpée du serveur : tout verser dans « rue » rendait
    // l'intervenant inutilisable pour un courrier.
    const a = choix.adresse_detaillee || {};
    const personnePhysique = choix.type === 'CP' || choix.type === 'AG';
    onChange({
      ...valeurs,
      name_company: personnePhysique ? valeurs.name_company : choix.nom,
      name: personnePhysique ? choix.nom : valeurs.name,
      role2: choix.est_lie ? 'Agent' : 'Courtier',
      reference: choix.matricule,
      number: a.numero || valeurs.number,
      street: a.rue || choix.adresse || valeurs.street,
      postcode: a.code_postal || valeurs.postcode,
      city: a.localite || valeurs.city,
      country: valeurs.country || 'Luxembourg',
    });
  };
  const champ = (nom, label, props = {}) => (
    <TextField
      label={label}
      value={valeurs[nom] ?? ''}
      onChange={(e) => onChange({ ...valeurs, [nom]: e.target.value })}
      fullWidth
      {...props}
    />
  );

  return (
    <Dialog open={ouvert} onClose={onFermer} fullWidth maxWidth="sm">
      <DialogTitle>
        {valeurs.id ? 'Modifier un intervenant' : 'Ajouter un intervenant'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erreur && <Alert severity="error">{erreur}</Alert>}

          {champ('role1', 'À quel titre intervient cette personne ?', {
            select: true,
            helperText: role?.aide || ' ',
            onChange: (e) => onChange({ ...valeurs, role1: e.target.value,
                                        role2: '' }),
            children: ROLES.map((r) => (
              <MenuItem key={r.valeur} value={r.valeur}>{r.libelle}</MenuItem>
            )),
          })}

          {registre && (
            <Autocomplete
              options={trouves}
              filterOptions={(o) => o}
              inputValue={recherche}
              onInputChange={(e, v) => setRecherche(v)}
              onChange={(e, choix) => reprendreDuRegistre(choix)}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.nom || '')}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    <Typography variant="body2">{o.nom}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.type_libelle} · {o.matricule}
                      {o.compagnies_noms?.length > 0
                        && ` · ${o.compagnies_noms.slice(0, 2).join(', ')}`}
                    </Typography>
                  </Box>
                </li>
              )}
              noOptionsText={recherche.length < 2
                ? 'Tapez au moins deux lettres'
                : 'Aucun intermédiaire de ce nom au registre'}
              renderInput={(params) => (
                <TextField {...params}
                  label="Le chercher au registre officiel"
                  placeholder="Nom du bureau ou de la personne…"
                  helperText="Registre du Commissariat aux Assurances. Sa qualité — courtier ou agent — en est reprise." />
              )}
            />
          )}

          {estCompagnie && (
            <Autocomplete
              options={compagnies}
              value={compagnies.find((c) => c.name === valeurs.name_company) || null}
              onChange={(e, choix) => onChange({
                ...valeurs,
                name_company: choix?.name || '',
                phone: choix?.phone || valeurs.phone,
                // L'adresse du référentiel est l'adresse générale ; celle que
                // l'assuré saisira ici est le service qui suit son sinistre.
                mail: valeurs.mail || choix?.email || '',
              })}
              getOptionLabel={(c) => c.name || ''}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="Aucune compagnie de ce nom"
              renderInput={(params) => (
                <TextField {...params} label="Choisir la compagnie"
                  placeholder="Tapez les premières lettres…"
                  helperText="Complétez ensuite l'adresse du service qui suit votre sinistre : elle prime sur l'adresse générale." />
              )}
            />
          )}

          {role?.precisions?.length > 0 && champ('role2', 'Précision', {
            select: true,
            children: [
              <MenuItem key="" value="">Sans précision</MenuItem>,
              ...role.precisions.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              )),
            ],
          })}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {champ('firstname', 'Prénom')}
            {champ('name', 'Nom')}
          </Stack>

          {champ('name_company', 'Société', {
            helperText: "Si l'intervenant est une entreprise." })}
          {valeurs.name_company && champ('entreprise_number', "Numéro d'entreprise")}

          {champ('reference', 'Référence', {
            helperText: "Le numéro sous lequel cet intervenant suit le dossier : matricule, numéro de gestionnaire, référence de devis." })}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {champ('mail', 'Courriel', { type: 'email' })}
            {champ('phone', 'Téléphone')}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {champ('street', 'Rue', { sx: { flex: 2 } })}
            {champ('number', 'N°', { sx: { flex: 1 } })}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {champ('postcode', 'Code postal', { sx: { flex: 1 } })}
            {champ('city', 'Localité', { sx: { flex: 2 } })}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFermer}>Annuler</Button>
        <Button variant="contained" onClick={onEnregistrer} disabled={occupe}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Deuxième étape : qui intervient dans ce sinistre.
 *
 * Un dossier ne tient pas qu'aux dommages : sans le tiers lésé, une
 * responsabilité civile n'a pas d'objet, et sans l'intermédiaire personne ne
 * sait à qui la réclamation est adressée.
 */
export default function Intervenants({ expertiseId, onSuivant, onRetour,
                                       onEtape }) {
  const [personnes, setPersonnes] = useState(null);
  const [erreur, setErreur] = useState('');
  const [erreurForm, setErreurForm] = useState('');
  const [edition, setEdition] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [invite, setInvite] = useState(null);
  const [avis, setAvis] = useState(null);
  const [titulaire, setTitulaire] = useState(false);

  const recharger = React.useCallback(() => {
    listerIntervenants(expertiseId)
      .then(setPersonnes)
      .catch(() => setErreur('Impossible de charger les intervenants.'));
  }, [expertiseId]);

  useEffect(() => { recharger(); }, [recharger]);

  // Les dossiers reliés ne se lisent que par le titulaire : c'est notre façon
  // de savoir si ce dossier est le nôtre ou s'il nous est partagé.
  useEffect(() => {
    listerLiaisons(expertiseId)
      .then(() => setTitulaire(true))
      .catch(() => setTitulaire(false));
  }, [expertiseId]);

  // Ce qui est déjà sur la plateforme n'a pas à être invité de nouveau.
  const invitable = (p) => !p.plateforme
    || ['invite', 'retire'].includes(p.plateforme.etat);

  const enregistrer = async () => {
    setOccupe(true);
    setErreurForm('');
    try {
      const { id, ...champs } = edition;
      if (id) await modifierIntervenant(id, champs);
      else await creerIntervenant(expertiseId, champs);
      setEdition(null);
      recharger();
    } catch {
      setErreurForm("L'enregistrement n'a pas abouti. Réessayez.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async (personne) => {
    setErreur('');
    try {
      await supprimerIntervenant(personne.id);
      recharger();
    } catch {
      setErreur("La suppression n'a pas abouti.");
    }
  };

  return (
    <Layout
      etape="intervenants"
      titre="Les intervenants"
      introduction="Qui d'autre est concerné par ce sinistre ? Votre intermédiaire, un voisin lésé, l'entreprise qui a fait le devis. Vous pouvez y revenir à tout moment."
      onEtape={onEtape}
      actions={
        <>
          <Button onClick={onRetour}>Retour</Button>
          <Button variant="contained" size="large" onClick={onSuivant}>
            Continuer
          </Button>
        </>
      }
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}
      {avis && (
        <Alert severity={avis.severite} onClose={() => setAvis(null)}
               sx={{ mb: 3, wordBreak: 'break-word' }}>
          {avis.texte}
        </Alert>
      )}
      {personnes === null && !erreur && <CircularProgress size={28} />}

      <Stack spacing={2}>
        {personnes?.map((p) => (
          <Paper key={p.id} variant="outlined" sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {nomAffiche(p)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[p.mail, p.phone, [p.postcode, p.city].filter(Boolean).join(' ')]
                    .filter(Boolean).join(' · ') || 'Aucune coordonnée'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}
                       flexWrap="wrap" useFlexGap>
                  <Chip size="small" variant="outlined"
                        label={roleDe(p.role1)?.libelle || p.role1 || 'Rôle non précisé'} />
                  {p.role2 && <Chip size="small" variant="outlined" label={p.role2} />}
                  {p.reference && (
                    <Chip size="small" variant="outlined"
                          label={`réf. ${p.reference}`} />
                  )}
                  {p.plateforme && (
                    <Chip size="small"
                          color={COULEURS_PLATEFORME[p.plateforme.etat] || 'default'}
                          label={`${p.plateforme.vous ? 'Vous · ' : ''}${
                            p.plateforme.etat === 'invite' ? '' : 'Sur Iziclai · '}${
                            p.plateforme.libelle}`} />
                  )}
                </Stack>
              </Box>
              {invitable(p) && (
                <Button size="small" variant="outlined"
                        onClick={() => { setAvis(null); setInvite(p); }}>
                  {p.plateforme?.etat === 'invite' ? 'Réinviter' : 'Inviter'}
                </Button>
              )}
              <IconButton aria-label={`Modifier ${nomAffiche(p)}`}
                          onClick={() => setEdition({ ...VIDE, ...p })}>
                <MdEdit />
              </IconButton>
              <IconButton aria-label={`Supprimer ${nomAffiche(p)}`}
                          onClick={() => supprimer(p)}>
                <MdDelete />
              </IconButton>
            </Stack>
          </Paper>
        ))}

        {personnes?.length === 0 && (
          <Alert severity="info">
            Aucun intervenant n'est encore renseigné.
          </Alert>
        )}

        <Paper variant="outlined"
               sx={{ p: 3, borderStyle: 'dashed', textAlign: 'center' }}>
          <Button variant="contained"
                  onClick={() => { setErreurForm(''); setEdition({ ...VIDE }); }}>
            Ajouter un intervenant
          </Button>
        </Paper>
      </Stack>

      {invite && (
        <Invitation personne={invite} titulaire={titulaire}
                    expertiseId={expertiseId}
                    onFermer={() => setInvite(null)}
                    onInvite={(a) => { setInvite(null); setAvis(a); recharger(); }} />
      )}

      <Formulaire
        ouvert={edition !== null}
        valeurs={edition || VIDE}
        onChange={setEdition}
        onFermer={() => setEdition(null)}
        onEnregistrer={enregistrer}
        occupe={occupe}
        erreur={erreurForm}
      />
    </Layout>
  );
}
